import { isValidSearchText, searchTextToSend } from '@/lib/search-text';
import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export const NATIVE_PROPERTY_PAGE_SIZE = 50;
export interface NativeProperty {
  id: string;
  name: string;
  code: string;
  version: number;
  authority: 'CORE_OWNED' | 'LEGACY_IMPORT' | null;
  created_at: string;
  structure_revision: number;
  application_enabled: boolean;
}
export interface NativeCatalogType {
  id: string;
  property_id: string;
  name: string;
  revision: number;
  authority: 'CORE_OWNED' | 'LEGACY_IMPORT' | null;
  /** 가져온 Room Type은 값이 없을 수 있다 — null은 '설정 없음'이며 빈 객체로 바꾸지 않는다. */
  specifications: {
    schema_version: 1;
    furniture?: string[] | null;
    facilities?: string[] | null;
  } | null;
  description: {
    schema_version: 1;
    texts: {
      purpose: 'DISPLAY_NAME' | 'DESCRIPTION';
      language: 'ko' | 'en' | 'und';
      value: string | null;
      source_order: number | null;
    }[];
  } | null;
}
export interface NativeCatalogUnit {
  id: string;
  room_id: string;
  name: string;
  physical_capacity: 1;
  revision: number;
  authority: 'CORE_OWNED' | 'LEGACY_IMPORT' | null;
}
export interface NativeCatalogRoom {
  id: string;
  property_id: string;
  room_type_id: string;
  name: string;
  floor: number | null;
  revision: number;
  authority: 'CORE_OWNED' | 'LEGACY_IMPORT' | null;
  topology_revision: number;
  selected: boolean | null;
  selection_version: number | null;
  units: NativeCatalogUnit[];
}
export interface NativeCatalog {
  property: NativeProperty;
  room_types: NativeCatalogType[];
  rooms: NativeCatalogRoom[];
}
export interface NativePage<T> {
  data: T;
  next_cursor: string | null;
}
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: 'Core 지점 응답을 확인하지 못했습니다.',
  });
const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const exact = (v: Record<string, unknown>, keys: string[]) =>
  Object.keys(v).length === keys.length && keys.every((key) => key in v);
function validSpecifications(value: unknown): boolean {
  if (value === null) return true;
  if (
    !record(value) ||
    value.schema_version !== 1 ||
    Object.keys(value).some((key) => !['schema_version', 'furniture', 'facilities'].includes(key))
  )
    return false;
  return ['furniture', 'facilities'].every((key) => {
    if (!(key in value)) return true;
    const field = value[key];
    return (
      field === null || (Array.isArray(field) && field.every((item) => typeof item === 'string'))
    );
  });
}
function validDescription(value: unknown): boolean {
  if (value === null) return true;
  return (
    record(value) &&
    exact(value, ['schema_version', 'texts']) &&
    value.schema_version === 1 &&
    Array.isArray(value.texts) &&
    value.texts.every(
      (item) =>
        record(item) &&
        exact(item, ['purpose', 'language', 'value', 'source_order']) &&
        ['DISPLAY_NAME', 'DESCRIPTION'].includes(String(item.purpose)) &&
        ['ko', 'en', 'und'].includes(String(item.language)) &&
        (item.value === null || typeof item.value === 'string') &&
        (item.source_order === null || Number.isSafeInteger(item.source_order)),
    )
  );
}
const validProperty = (v: unknown): v is NativeProperty =>
  record(v) &&
  exact(v, [
    'id',
    'name',
    'code',
    'version',
    'authority',
    'created_at',
    'structure_revision',
    'application_enabled',
  ]) &&
  isUuid(v.id) &&
  typeof v.name === 'string' &&
  typeof v.code === 'string' &&
  Number.isSafeInteger(v.version) &&
  Number(v.version) > 0 &&
  (v.authority === 'CORE_OWNED' || v.authority === 'LEGACY_IMPORT' || v.authority === null) &&
  isOffsetDateTime(v.created_at) &&
  Number.isSafeInteger(v.structure_revision) &&
  Number(v.structure_revision) > 0 &&
  typeof v.application_enabled === 'boolean';
function validCatalog(v: unknown): v is NativeCatalog {
  if (
    !record(v) ||
    !exact(v, ['property', 'room_types', 'rooms']) ||
    !validProperty(v.property) ||
    !Array.isArray(v.room_types) ||
    !Array.isArray(v.rooms) ||
    v.rooms.length > NATIVE_PROPERTY_PAGE_SIZE ||
    v.room_types.length > 500
  )
    return false;
  const propertyId = v.property.id;
  const types = v.room_types;
  if (
    !types.every(
      (t) =>
        record(t) &&
        exact(t, [
          'id',
          'property_id',
          'name',
          'revision',
          'authority',
          'specifications',
          'description',
        ]) &&
        isUuid(t.id) &&
        t.property_id === propertyId &&
        typeof t.name === 'string' &&
        Number.isSafeInteger(t.revision) &&
        (t.authority === 'CORE_OWNED' || t.authority === 'LEGACY_IMPORT' || t.authority === null) &&
        validSpecifications(t.specifications) &&
        validDescription(t.description),
    )
  )
    return false;
  const typeIds = new Set(types.map((t) => (t as NativeCatalogType).id));
  if (typeIds.size !== types.length) return false;
  return v.rooms.every(
    (r) =>
      record(r) &&
      exact(r, [
        'id',
        'property_id',
        'room_type_id',
        'name',
        'floor',
        'revision',
        'authority',
        'topology_revision',
        'selection_version',
        'selected',
        'units',
      ]) &&
      isUuid(r.id) &&
      r.property_id === propertyId &&
      typeIds.has(String(r.room_type_id)) &&
      typeof r.name === 'string' &&
      (r.floor === null || Number.isSafeInteger(r.floor)) &&
      Number.isSafeInteger(r.revision) &&
      (r.authority === 'CORE_OWNED' || r.authority === 'LEGACY_IMPORT' || r.authority === null) &&
      Number.isSafeInteger(r.topology_revision) &&
      ((r.selected === null && r.selection_version === null) ||
        (typeof r.selected === 'boolean' && Number.isSafeInteger(r.selection_version))) &&
      Array.isArray(r.units) &&
      r.units.every(
        (u) =>
          record(u) &&
          exact(u, ['id', 'room_id', 'name', 'physical_capacity', 'revision', 'authority']) &&
          isUuid(u.id) &&
          u.room_id === r.id &&
          typeof u.name === 'string' &&
          u.physical_capacity === 1 &&
          Number.isSafeInteger(u.revision) &&
          (u.authority === 'CORE_OWNED' || u.authority === 'LEGACY_IMPORT' || u.authority === null),
      ),
  );
}
function envelope<T>(value: unknown, data: (v: unknown) => v is T): NativePage<T> {
  if (
    !record(value) ||
    !('data' in value) ||
    !('next_cursor' in value) ||
    !(
      value.next_cursor === null ||
      (typeof value.next_cursor === 'string' && value.next_cursor.length > 0)
    ) ||
    !data(value.data)
  )
    throw invalid();
  return { data: value.data, next_cursor: value.next_cursor };
}
export async function readNativeProperties(
  client: ServiceClient,
  cursor: string | null,
  direction: 'asc' | 'desc',
  signal?: AbortSignal,
): Promise<NativePage<NativeProperty[]>> {
  if (cursor !== null && (!cursor || typeof cursor !== 'string')) throw invalid();
  const value = await client.get<unknown>('admin/native-properties', {
    params: {
      limit: NATIVE_PROPERTY_PAGE_SIZE,
      sort_direction: direction,
      cursor: cursor ?? undefined,
    },
    preserveEnvelope: true,
    signal,
  });
  return envelope(
    value,
    (v): v is NativeProperty[] =>
      Array.isArray(v) && v.length <= NATIVE_PROPERTY_PAGE_SIZE && v.every(validProperty),
  );
}
export async function readNativeCatalog(
  client: ServiceClient,
  propertyId: string,
  cursor: string | null,
  signal?: AbortSignal,
  options: {
    roomTypeId?: string | null;
    roomId?: string | null;
    sortBy?: 'floor' | 'name';
    sortDirection?: 'asc' | 'desc';
    /** Room 또는 Unit 이름 부분 일치. trim 뒤 1~100자만 보낸다(서버는 빈 값·초과를 422로 거부한다). */
    q?: string | null;
    /** 정확한 층(int32, 0·음수 포함). `floorIsNull`과 함께 쓰지 않는다. */
    floor?: number | null;
    /** 층 미지정(NULL) Room만. 서버는 true만 받는다. */
    floorIsNull?: boolean;
  } = {},
): Promise<NativePage<NativeCatalog>> {
  const { roomTypeId, roomId, sortBy = 'floor', sortDirection = 'asc' } = options;
  const q = searchTextToSend(options.q);
  const floor = options.floor ?? null;
  const floorIsNull = options.floorIsNull === true;
  if (
    (q !== null && !isValidSearchText(q)) ||
    (floor !== null && (!Number.isInteger(floor) || floor < -(2 ** 31) || floor >= 2 ** 31)) ||
    (floor !== null && floorIsNull) ||
    !isUuid(propertyId) ||
    (cursor !== null && !cursor) ||
    (roomTypeId != null && !isUuid(roomTypeId)) ||
    (roomId != null && !isUuid(roomId)) ||
    !['floor', 'name'].includes(sortBy) ||
    !['asc', 'desc'].includes(sortDirection)
  )
    throw invalid();
  const value = await client.get<unknown>(`admin/native-properties/${propertyId}/catalog`, {
    params: {
      limit: NATIVE_PROPERTY_PAGE_SIZE,
      cursor: cursor ?? undefined,
      room_type_id: roomTypeId ?? undefined,
      room_id: roomId ?? undefined,
      sort_by: sortBy,
      sort_direction: sortDirection,
      q: q ?? undefined,
      floor: floor ?? undefined,
      floor_is_null: floorIsNull ? 'true' : undefined,
    },
    preserveEnvelope: true,
    signal,
  });
  const result = envelope(
    value,
    (v): v is NativeCatalog =>
      validCatalog(v) && v.property.id.toLowerCase() === propertyId.toLowerCase(),
  );
  if (
    (roomId != null &&
      (result.next_cursor !== null ||
        result.data.rooms.length !== 1 ||
        result.data.rooms[0]?.id !== roomId)) ||
    (roomTypeId != null && result.data.rooms.some((room) => room.room_type_id !== roomTypeId)) ||
    (floor !== null && result.data.rooms.some((room) => room.floor !== floor)) ||
    (floorIsNull && result.data.rooms.some((room) => room.floor !== null))
  )
    throw invalid();
  return result;
}

export interface NativeCatalogFloor {
  /** null은 층 미지정이며 0과 다르다. */
  floor: number | null;
  room_count: number;
}
const INT32_MIN = -(2 ** 31);
/** 지점(+타입)의 현재 미삭제 Room이 실제로 있는 층. 층 오름차순, 층 미지정(NULL)은 마지막 한 항목이다. */
export async function readNativeCatalogFloors(
  client: ServiceClient,
  propertyId: string,
  roomTypeId?: string | null,
  signal?: AbortSignal,
): Promise<NativeCatalogFloor[]> {
  if (!isUuid(propertyId) || (roomTypeId != null && !isUuid(roomTypeId))) throw invalid();
  const value = await client.get<unknown>(`admin/native-properties/${propertyId}/catalog/floors`, {
    params: { room_type_id: roomTypeId ?? undefined },
    signal,
  });
  if (!record(value) || !exact(value, ['floors']) || !Array.isArray(value.floors)) throw invalid();
  const floors = value.floors.map((row): NativeCatalogFloor => {
    if (
      !record(row) ||
      !exact(row, ['floor', 'room_count']) ||
      !(
        row.floor === null ||
        (Number.isInteger(row.floor) &&
          Number(row.floor) >= INT32_MIN &&
          Number(row.floor) < 2 ** 31)
      ) ||
      !Number.isSafeInteger(row.room_count) ||
      Number(row.room_count) < 1
    )
      throw invalid();
    return { floor: row.floor as number | null, room_count: row.room_count as number };
  });
  const seen = new Set<number | null>();
  floors.forEach((row, index) => {
    const previous = floors[index - 1];
    if (
      seen.has(row.floor) ||
      (row.floor === null && index !== floors.length - 1) ||
      (previous?.floor != null && row.floor !== null && row.floor <= previous.floor)
    )
      throw invalid();
    seen.add(row.floor);
  });
  return floors;
}

export interface DeletedNativeProperty {
  id: string;
  name: string;
  code: string;
  deleted_at: string;
}
/** Core soft-deleted rows only; source exclusions are not Core properties. */
export async function readDeletedNativeProperties(
  client: ServiceClient,
  cursor: string | null,
  direction: 'asc' | 'desc',
  signal?: AbortSignal,
): Promise<NativePage<DeletedNativeProperty[]>> {
  if (cursor !== null && !cursor) throw invalid();
  const value = await client.get<unknown>('admin/native-properties/deleted', {
    params: {
      limit: NATIVE_PROPERTY_PAGE_SIZE,
      sort_direction: direction,
      cursor: cursor ?? undefined,
    },
    preserveEnvelope: true,
    signal,
  });
  return envelope(
    value,
    (v): v is DeletedNativeProperty[] =>
      Array.isArray(v) &&
      v.length <= NATIVE_PROPERTY_PAGE_SIZE &&
      v.every(
        (row) =>
          record(row) &&
          exact(row, ['id', 'name', 'code', 'deleted_at']) &&
          isUuid(row.id) &&
          typeof row.name === 'string' &&
          typeof row.code === 'string' &&
          isOffsetDateTime(row.deleted_at),
      ),
  );
}

export type NativePropertyBasic = Pick<
  NativeProperty,
  'id' | 'name' | 'code' | 'version' | 'structure_revision' | 'application_enabled'
>;

/** Existing AssetData property GET; revision is the same CAS version used by application settings. */
export async function readNativeProperty(
  client: ServiceClient,
  id: string,
  signal?: AbortSignal,
): Promise<NativePropertyBasic> {
  if (!isUuid(id)) throw invalid();
  const value = await client.get<unknown>(`admin/properties/${id}`, { signal });
  const required = ['id', 'name', 'code', 'revision', 'structure_revision', 'application_enabled'];
  const nullable = [
    'property_id',
    'room_type_id',
    'room_id',
    'floor',
    'topology_revision',
    'room_topology_revision',
    'physical_capacity',
    'unit_presentation',
    'specifications',
    'description',
  ];
  if (
    !record(value) ||
    !required.every((key) => Object.hasOwn(value, key)) ||
    Object.keys(value).some(
      (key) => !required.includes(key) && (!nullable.includes(key) || value[key] !== null),
    ) ||
    value.id !== id ||
    typeof value.name !== 'string' ||
    typeof value.code !== 'string' ||
    !Number.isSafeInteger(value.revision) ||
    Number(value.revision) < 1 ||
    !Number.isSafeInteger(value.structure_revision) ||
    Number(value.structure_revision) < 1 ||
    typeof value.application_enabled !== 'boolean'
  )
    throw invalid();
  return {
    id,
    name: value.name,
    code: value.code,
    version: Number(value.revision),
    structure_revision: Number(value.structure_revision),
    application_enabled: value.application_enabled,
  };
}

export type NativeTypeDetails = Omit<NativeCatalogType, 'authority'>;
export type NativeTypeBrief = Pick<NativeCatalogType, 'id' | 'property_id' | 'name' | 'revision'>;
export interface NativeTypeBriefPage {
  items: NativeTypeBrief[];
  next_cursor: string | null;
}
const validBriefCursor = (cursor: unknown): cursor is string =>
  typeof cursor === 'string' &&
  cursor.length >= 1 &&
  cursor.length <= 2048 &&
  /^[A-Za-z0-9_-]+$/.test(cursor);
export async function readNativeTypeBriefs(
  client: ServiceClient,
  propertyId: string,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<NativeTypeBriefPage> {
  if (!isUuid(propertyId) || (cursor !== null && !validBriefCursor(cursor))) throw invalid();
  const value = await client.get<unknown>(`admin/properties/${propertyId}/room-types`, {
    params: { limit: 50, cursor: cursor ?? undefined },
    signal,
  });
  if (
    !record(value) ||
    !exact(value, ['items', 'next_cursor']) ||
    !Array.isArray(value.items) ||
    value.items.length > 50 ||
    (value.next_cursor !== null && !validBriefCursor(value.next_cursor)) ||
    !value.items.every(
      (item) =>
        record(item) &&
        exact(item, ['id', 'property_id', 'name', 'revision']) &&
        isUuid(item.id) &&
        item.property_id === propertyId &&
        typeof item.name === 'string' &&
        Number.isSafeInteger(item.revision) &&
        Number(item.revision) > 0,
    ) ||
    new Set(value.items.map((item) => (item as NativeTypeBrief).id)).size !== value.items.length
  )
    throw invalid();
  return value as unknown as NativeTypeBriefPage;
}
export async function readNativeType(
  client: ServiceClient,
  propertyId: string,
  typeId: string,
  signal?: AbortSignal,
): Promise<NativeTypeDetails> {
  if (!isUuid(propertyId) || !isUuid(typeId)) throw invalid();
  const value = await client.get<unknown>(`admin/room-types/${typeId}`, { signal });
  const required = ['id', 'name', 'revision', 'property_id', 'specifications', 'description'];
  const nullable = [
    'code',
    'structure_revision',
    'application_enabled',
    'room_type_id',
    'room_id',
    'floor',
    'topology_revision',
    'room_topology_revision',
    'physical_capacity',
    'unit_presentation',
  ];
  if (
    !record(value) ||
    !required.every((key) => Object.hasOwn(value, key)) ||
    Object.keys(value).some(
      (key) => !required.includes(key) && (!nullable.includes(key) || value[key] !== null),
    ) ||
    value.id !== typeId ||
    value.property_id !== propertyId ||
    typeof value.name !== 'string' ||
    !Number.isSafeInteger(value.revision) ||
    Number(value.revision) < 1 ||
    !validSpecifications(value.specifications) ||
    !validDescription(value.description)
  )
    throw invalid();
  return {
    id: typeId,
    property_id: propertyId,
    name: value.name,
    revision: Number(value.revision),
    specifications: value.specifications as NativeCatalogType['specifications'],
    description: value.description as NativeCatalogType['description'],
  };
}
